import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Wallet,
  Users,
  Landmark,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  // Business Information
  corporation_name: "",
  dba_name: "",
  physical_address: "",
  city: "",
  state: "",
  zip: "",
  phone_number: "",
  email_address: "",
  url: "",
  business_contact: "",
  products_services: "",

  // Financial Profile
  state_incorporated: "",
  month_year_started: "",
  ownership_type: "",
  number_of_employees: "",
  federal_tax_id: "",
  average_monthly_volume: "",
  average_transaction: "",
  highest_transaction: "",
  equipment_gateway: "",
  pct_swiped: "",
  pct_keyed: "",
  pct_phone: "",
  pct_internet: "",

  // Owner #1
  owner1_full_name: "",
  owner1_title: "",
  owner1_ownership_pct: "",
  owner1_dob: "",
  owner1_ssn: "",
  owner1_address: "",
  owner1_city: "",
  owner1_state: "",
  owner1_zip: "",
  owner1_cell_phone: "",
  owner1_drivers_license: "",

  // Owner #2 (optional)
  owner2_full_name: "",
  owner2_title: "",
  owner2_ownership_pct: "",
  owner2_dob: "",
  owner2_ssn: "",
  owner2_address: "",
  owner2_city: "",
  owner2_state: "",
  owner2_zip: "",
  owner2_cell_phone: "",
  owner2_drivers_license: "",

  // Deposit Account
  bank_name: "",
  routing_number: "",
  account_number: "",

  // Acknowledgement
  signature_name: "",
  signature_title: "",
  signature_date: new Date().toISOString().slice(0, 10),
  acknowledged: false,
};

const SECTIONS = [
  { id: "business", n: "01", title: "Business Information", icon: Building2 },
  { id: "financial", n: "02", title: "Financial Profile", icon: Wallet },
  { id: "ownership", n: "03", title: "Ownership", icon: Users },
  { id: "deposit", n: "04", title: "Deposit Account", icon: Landmark },
  { id: "sign", n: "05", title: "Acknowledge & Sign", icon: ShieldCheck },
];

export default function MerchantApplicationPage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [showOwner2, setShowOwner2] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged) {
      setStatus({
        state: "error",
        msg: "Please acknowledge the application before submitting.",
      });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND_URL}/api/merchant-application`, form);
      setStatus({
        state: "success",
        msg: "Application received. A UMG underwriting specialist will reach out within 1 business day to confirm next steps.",
      });
      setForm(INITIAL);
      setShowOwner2(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setStatus({
        state: "error",
        msg: typeof detail === "string" ? detail : "Please check your inputs and try again.",
      });
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative border-b border-ink-900 bg-white" data-testid="apply-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid opacity-50" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-28">
          <div className="lg:col-span-9">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 bg-accent" />
              <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500">
                /// File 06 — Merchant Application
              </span>
            </div>
            <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-ink-900 sm:text-7xl lg:text-8xl">
              High-Risk
              <br />
              <span className="text-outline">Merchant Account</span>
              <br />
              <span className="text-accent">Application.</span>
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-sm leading-relaxed text-ink-500">
              Complete the application below to get started. All information is
              kept strictly confidential and used only for underwriting.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION INDEX STRIP */}
      <section className="border-b border-ink-900 bg-ink-50" data-testid="apply-section-index">
        <div className="container-wide">
          <ul className="grid grid-cols-2 divide-ink-900 md:grid-cols-3 lg:grid-cols-6 lg:divide-x">
            {SECTIONS.map(({ id, n, title, icon: Icon }) => (
              <li key={id} className="flex items-center gap-3 px-4 py-5">
                <Icon className="h-4 w-4 text-accent" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-ultra text-ink-400">
                    {n}
                  </div>
                  <a
                    href={`#${id}`}
                    className="font-mono text-[11px] font-semibold uppercase tracking-wide2 text-ink-900 hover:text-accent"
                  >
                    {title}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <form
        onSubmit={onSubmit}
        className="border-b border-ink-900 bg-white"
        data-testid="merchant-app-form"
      >
        {/* SECTION 01 — BUSINESS INFORMATION */}
        <FormSection id="business" n="01" title="Business Information" subtitle="Tell us about the entity that will process payments.">
          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="Corporation Name" required>
              <input name="corporation_name" required value={form.corporation_name} onChange={onChange} className="input-brutal" placeholder="Acme Holdings, Inc." data-testid="apply-corporation-name" />
            </Field>
            <Field label="DBA Name">
              <input name="dba_name" value={form.dba_name} onChange={onChange} className="input-brutal" placeholder="Acme" data-testid="apply-dba-name" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Physical Address" required>
                <input name="physical_address" required value={form.physical_address} onChange={onChange} className="input-brutal" placeholder="123 Market St, Suite 400" data-testid="apply-physical-address" />
              </Field>
            </div>
            <Field label="City" required>
              <input name="city" required value={form.city} onChange={onChange} className="input-brutal" data-testid="apply-city" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" required>
                <input name="state" required value={form.state} onChange={onChange} className="input-brutal" placeholder="NY" data-testid="apply-state" />
              </Field>
              <Field label="ZIP" required>
                <input name="zip" required value={form.zip} onChange={onChange} className="input-brutal" placeholder="10001" data-testid="apply-zip" />
              </Field>
            </div>
            <Field label="Phone Number" required>
              <input name="phone_number" required value={form.phone_number} onChange={onChange} className="input-brutal" placeholder="+1 555 …" data-testid="apply-phone" />
            </Field>
            <Field label="Email Address" required>
              <input type="email" name="email_address" required value={form.email_address} onChange={onChange} className="input-brutal" placeholder="founder@company.com" data-testid="apply-email" />
            </Field>
            <Field label="Website (URL)">
              <input name="url" value={form.url} onChange={onChange} className="input-brutal" placeholder="https://" data-testid="apply-url" />
            </Field>
            <Field label="Business Contact" required>
              <input name="business_contact" required value={form.business_contact} onChange={onChange} className="input-brutal" placeholder="Primary contact name" data-testid="apply-business-contact" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Products / Services Sold" required>
                <textarea name="products_services" required rows={3} value={form.products_services} onChange={onChange} className="input-brutal resize-y" placeholder="Describe what you sell, who buys it, and how it ships." data-testid="apply-products-services" />
              </Field>
            </div>
          </div>
        </FormSection>

        {/* SECTION 02 — FINANCIAL PROFILE */}
        <FormSection id="financial" n="02" title="Financial Profile" subtitle="Helps underwriting size the relationship.">
          <div className="grid gap-8 sm:grid-cols-2">
            <Field label="State Incorporated" required>
              <input name="state_incorporated" required value={form.state_incorporated} onChange={onChange} className="input-brutal" placeholder="DE" data-testid="apply-state-incorporated" />
            </Field>
            <Field label="Month / Year Started" required>
              <input name="month_year_started" required value={form.month_year_started} onChange={onChange} className="input-brutal" placeholder="MM/YYYY" data-testid="apply-month-year-started" />
            </Field>
            <Field label="Ownership Type" required>
              <select name="ownership_type" required value={form.ownership_type} onChange={onChange} className="input-brutal" data-testid="apply-ownership-type">
                <option value="">Select…</option>
                <option>Sole Proprietor</option>
                <option>LLC</option>
                <option>S-Corp</option>
                <option>C-Corp</option>
                <option>Partnership</option>
                <option>Non-Profit</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Number of Employees">
              <input name="number_of_employees" value={form.number_of_employees} onChange={onChange} className="input-brutal" placeholder="e.g. 12" data-testid="apply-employees" />
            </Field>
            <Field label="Federal Tax ID (EIN)" required>
              <input name="federal_tax_id" required value={form.federal_tax_id} onChange={onChange} className="input-brutal" placeholder="00-0000000" data-testid="apply-ein" />
            </Field>
            <Field label="Average Monthly Sales Volume" required>
              <input name="average_monthly_volume" required value={form.average_monthly_volume} onChange={onChange} className="input-brutal" placeholder="$" data-testid="apply-avg-volume" />
            </Field>
            <Field label="Average Transaction" required>
              <input name="average_transaction" required value={form.average_transaction} onChange={onChange} className="input-brutal" placeholder="$" data-testid="apply-avg-tx" />
            </Field>
            <Field label="Highest Transaction" required>
              <input name="highest_transaction" required value={form.highest_transaction} onChange={onChange} className="input-brutal" placeholder="$" data-testid="apply-highest-tx" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Equipment / Gateway">
                <input name="equipment_gateway" value={form.equipment_gateway} onChange={onChange} className="input-brutal" placeholder="e.g. NMI, Authorize.net, Stripe Terminal" data-testid="apply-equipment-gateway" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <div className="label-brutal mb-3">Percentage of Sales (must total 100%)</div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Field label="Swiped %">
                  <input name="pct_swiped" value={form.pct_swiped} onChange={onChange} className="input-brutal" placeholder="0" data-testid="apply-pct-swiped" />
                </Field>
                <Field label="Keyed %">
                  <input name="pct_keyed" value={form.pct_keyed} onChange={onChange} className="input-brutal" placeholder="0" data-testid="apply-pct-keyed" />
                </Field>
                <Field label="Phone %">
                  <input name="pct_phone" value={form.pct_phone} onChange={onChange} className="input-brutal" placeholder="0" data-testid="apply-pct-phone" />
                </Field>
                <Field label="Internet %">
                  <input name="pct_internet" value={form.pct_internet} onChange={onChange} className="input-brutal" placeholder="0" data-testid="apply-pct-internet" />
                </Field>
              </div>
            </div>
          </div>
        </FormSection>

        {/* SECTION 03 — OWNERSHIP */}
        <FormSection id="ownership" n="03" title="Ownership" subtitle="Information for each owner with ≥ 25% stake.">
          <OwnerBlock idx="1" form={form} onChange={onChange} required />
          {!showOwner2 && (
            <button
              type="button"
              onClick={() => setShowOwner2(true)}
              className="btn-secondary mt-10"
              data-testid="apply-add-owner-2"
            >
              + Add second owner
            </button>
          )}
          {showOwner2 && (
            <>
              <div className="my-12 h-px bg-ink-900" />
              <OwnerBlock idx="2" form={form} onChange={onChange} />
              <button
                type="button"
                onClick={() => setShowOwner2(false)}
                className="mt-8 font-mono text-[11px] uppercase tracking-wide2 text-ink-500 hover:text-accent"
                data-testid="apply-remove-owner-2"
              >
                — Remove second owner
              </button>
            </>
          )}
        </FormSection>

        {/* SECTION 04 — DEPOSIT ACCOUNT */}
        <FormSection id="deposit" n="04" title="Deposit Account" subtitle="Where settlements will be deposited.">
          <div className="grid gap-8 sm:grid-cols-3">
            <Field label="Bank Name" required>
              <input name="bank_name" required value={form.bank_name} onChange={onChange} className="input-brutal" placeholder="e.g. Chase" data-testid="apply-bank-name" />
            </Field>
            <Field label="Routing Number" required>
              <input name="routing_number" required value={form.routing_number} onChange={onChange} className="input-brutal" placeholder="9 digits" data-testid="apply-routing" />
            </Field>
            <Field label="Account Number" required>
              <input name="account_number" required value={form.account_number} onChange={onChange} className="input-brutal" placeholder="…" data-testid="apply-account" />
            </Field>
          </div>
        </FormSection>

        {/* SECTION 05 — ACKNOWLEDGE & SIGN */}
        <FormSection id="sign" n="05" title="Acknowledge & Sign" subtitle="Electronic signature has the same legal effect as a handwritten signature.">
          <div className="border border-ink-900 bg-ink-50 p-6">
            <p className="font-mono text-sm leading-relaxed text-ink-700">
              By signing below, the undersigned authorized signer (i) certifies
              that all information provided is true, complete, and accurate to
              the best of their knowledge; (ii) authorizes UMG Payments and its
              acquiring partners to verify any of the information provided,
              including credit and background checks on the business and its
              principal owners; and (iii) agrees to be contacted by UMG
              regarding this application.
            </p>
          </div>

          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            <Field label="Authorized Signer (Full Name)" required>
              <input name="signature_name" required value={form.signature_name} onChange={onChange} className="input-brutal" placeholder="Type full legal name" data-testid="apply-signature-name" />
            </Field>
            <Field label="Title" required>
              <input name="signature_title" required value={form.signature_title} onChange={onChange} className="input-brutal" placeholder="e.g. CEO" data-testid="apply-signature-title" />
            </Field>
            <Field label="Date" required>
              <input type="date" name="signature_date" required value={form.signature_date} onChange={onChange} className="input-brutal" data-testid="apply-signature-date" />
            </Field>
          </div>

          {/* Signature preview */}
          <div className="mt-10">
            <div className="label-brutal mb-2">Signature Preview</div>
            <div className="flex items-end border border-ink-900 bg-white px-6 pb-3 pt-10">
              <span
                className="font-display text-3xl italic text-ink-900 sm:text-4xl"
                style={{ fontFamily: "'Caveat', 'Brush Script MT', cursive" }}
                data-testid="apply-signature-preview"
              >
                {form.signature_name || "— your signature appears here —"}
              </span>
            </div>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-ultra text-ink-400">
              {form.signature_date} · electronically signed via umg.inc
            </p>
          </div>

          <label className="mt-10 flex items-start gap-4 border border-ink-900 bg-white p-5" data-testid="apply-acknowledge-wrapper">
            <input
              type="checkbox"
              name="acknowledged"
              required
              checked={form.acknowledged}
              onChange={onChange}
              className="mt-1 h-5 w-5 accent-[#FF3B30]"
              data-testid="apply-acknowledge"
            />
            <span className="font-mono text-sm leading-relaxed text-ink-700">
              I acknowledge that all information above is accurate, I am
              authorized to submit this application on behalf of the business,
              and I consent to UMG Payments contacting me about this
              application.
            </span>
          </label>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-ink-900 pt-8">
            <p className="max-w-md font-mono text-[11px] uppercase tracking-wide2 text-ink-400">
              Application submitted electronically. UMG operations team
              (ops@umg.inc) will follow up within 1 business day.
            </p>
            <button
              type="submit"
              disabled={status.state === "loading"}
              className="btn-primary"
              data-testid="apply-submit"
            >
              {status.state === "loading" ? "Submitting…" : "Submit Application"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {status.state === "success" && (
            <div
              className="mt-8 flex items-start gap-3 border border-ink-900 bg-ink-50 p-5"
              data-testid="apply-success"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-accent" />
              <span className="font-mono text-sm text-ink-900">{status.msg}</span>
            </div>
          )}
          {status.state === "error" && (
            <div
              className="mt-8 flex items-start gap-3 border border-accent bg-white p-5"
              data-testid="apply-error"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 text-accent" />
              <span className="font-mono text-sm text-accent">{status.msg}</span>
            </div>
          )}
        </FormSection>
      </form>
    </>
  );
}

function FormSection({ id, n, title, subtitle, children }) {
  return (
    <section
      id={id}
      className="border-b border-ink-900 bg-white scroll-mt-24"
      data-testid={`apply-section-${id}`}
    >
      <div className="container-wide grid gap-10 py-16 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-ultra text-accent">
              SECTION {n}
            </span>
            <span className="h-px flex-1 bg-ink-900" />
          </div>
          <h2 className="mt-4 font-display text-3xl font-black uppercase leading-tight tracking-tighter sm:text-4xl">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 font-mono text-sm leading-relaxed text-ink-500">
              {subtitle}
            </p>
          )}
        </div>
        <div className="lg:col-span-8">{children}</div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-brutal mb-2 block">
        {label}
        {required && <span className="text-accent"> *</span>}
      </span>
      {children}
    </label>
  );
}

function OwnerBlock({ idx, form, onChange, required = false }) {
  const k = (suffix) => `owner${idx}_${suffix}`;
  const v = (suffix) => form[k(suffix)];
  return (
    <div data-testid={`apply-owner-${idx}`}>
      <div className="mb-6 flex items-baseline gap-3">
        <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500">
          Owner #{idx}
        </span>
        <span className="h-px flex-1 bg-ink-100" />
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label="Full Name" required={required}>
          <input name={k("full_name")} required={required} value={v("full_name")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-name`} />
        </Field>
        <Field label="Title" required={required}>
          <input name={k("title")} required={required} value={v("title")} onChange={onChange} className="input-brutal" placeholder="e.g. CEO" data-testid={`apply-owner${idx}-title`} />
        </Field>
        <Field label="Ownership %" required={required}>
          <input name={k("ownership_pct")} required={required} value={v("ownership_pct")} onChange={onChange} className="input-brutal" placeholder="e.g. 60" data-testid={`apply-owner${idx}-pct`} />
        </Field>
        <Field label="Date of Birth" required={required}>
          <input type="date" name={k("dob")} required={required} value={v("dob")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-dob`} />
        </Field>
        <Field label="Social Security Number" required={required}>
          <input name={k("ssn")} required={required} value={v("ssn")} onChange={onChange} className="input-brutal" placeholder="xxx-xx-xxxx" data-testid={`apply-owner${idx}-ssn`} />
        </Field>
        <Field label="Driver's License #" required={required}>
          <input name={k("drivers_license")} required={required} value={v("drivers_license")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-dl`} />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Physical Address" required={required}>
            <input name={k("address")} required={required} value={v("address")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-address`} />
          </Field>
        </div>
        <Field label="City" required={required}>
          <input name={k("city")} required={required} value={v("city")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-city`} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="State" required={required}>
            <input name={k("state")} required={required} value={v("state")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-state`} />
          </Field>
          <Field label="ZIP" required={required}>
            <input name={k("zip")} required={required} value={v("zip")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-zip`} />
          </Field>
        </div>
        <Field label="Cell Phone" required={required}>
          <input name={k("cell_phone")} required={required} value={v("cell_phone")} onChange={onChange} className="input-brutal" data-testid={`apply-owner${idx}-phone`} />
        </Field>
      </div>
    </div>
  );
}
